import { AssignmentDetailsCard, CreativeAssignmentCard } from '../../../common'
import { DescriptionOutlined, UploadFileOutlined } from '@mui/icons-material'
import { FC, Fragment, ReactNode, useMemo } from 'react'

import { AssignmentDTO } from 'models/assignment'
import { AssignmentStage } from 'constants/assignment'
import { DocumentUploadType } from 'constants/documents'
import { MUIButton } from 'components/common/MUIButton'
import { ProductDTO } from 'models/product'
import { ProductKind } from 'constants/product'
import { SendDocumentsToClient } from '../SendToClient'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useGalleryDocumentsContext } from '../../contexts'
import { useTranslation } from 'react-i18next'

interface AdminGalleryDocumentsPanelProps {
  /** Assignment ID */
  assignmentId: string
  /** Assignment */
  assignment?: AssignmentDTO
  /** Product of the assignment */
  product?: ProductDTO
  /** Callback to open the assignment detail drawer */
  onSeeDetails?: () => void
}

const typeIconMap: Record<DocumentUploadType, ReactNode> = {
  [DocumentUploadType.INPUT]: <UploadFileOutlined fontSize="large" />,
  [DocumentUploadType.OUTPUT]: <DescriptionOutlined fontSize="large" />,
}

/**
 * Admin gallery documents panel component
 * 
 * @example
 * <AdminGalleryDocumentsPanel assignmentId={assignmentId}/>
 */
export const AdminGalleryDocumentsPanel: FC<AdminGalleryDocumentsPanelProps> = ({ assignmentId, assignment, product, onSeeDetails }) => {
  const { t } = useTranslation(['gallery'])

  const {
    documentTypeSelect,
    isMessageThreadsVisible,
    setDocumentTypeSelect,
    setIsMessageThreadsVisible,
  } = useGalleryDocumentsContext()

  const canSendAssignmentToClient = useMemo(() => {
    return !!assignment?.stage && [
      AssignmentStage.REQUEST_SENT_TO_PROCESSOR,
      AssignmentStage.DOCUMENTS_RECEIVED_FROM_PROCESSOR,
      AssignmentStage.VISUALS_SENT_BY_EDITOR_TO_BKBN
    ].includes(assignment.stage)
  }, [assignment])

  const showSendToClient = useMemo(() => {
    return (product?.kind === ProductKind.MEASUREMENT_ON_SITE || product?.kind === ProductKind.AUTHORITIES_DOCUMENTS) && documentTypeSelect === DocumentUploadType.OUTPUT
  }, [documentTypeSelect, product])

  return (
    <Fragment>

      <AssignmentDetailsCard assignmentId={assignmentId} onSeeDetails={onSeeDetails} />

      <CreativeAssignmentCard />

      <div className="details">
        <div className="sticky">
          <div className="flex">
            <div className="documentTypeTabs">
              {Object.values(DocumentUploadType).map(type => (
                <MUIButton
                  key={type}
                  type={documentTypeSelect === type ? 'defaultPrimary' : 'secondaryBorder'}
                  startIcon={typeIconMap[type]}
                  onClick={() => {
                    setDocumentTypeSelect(type)
                  }}
                >
                  <span>{t(`document_type.${type}`)}</span>
                </MUIButton>
              )
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="details">
        <MUIButton
          startIcon={isMessageThreadsVisible
            ? <VisibilityOffOutlinedIcon fontSize="medium" />
            : <VisibilityOutlinedIcon fontSize="medium" />
          }
          onClick={() => setIsMessageThreadsVisible((prevState) => !prevState)}
        >
          {isMessageThreadsVisible ? t('button_hide_message_threads') : t('button_show_message_threads')}
        </MUIButton>
      </div>

      {showSendToClient &&
        <div className="details">
          <SendDocumentsToClient
            canSendAssignmentDocumentsToClient={canSendAssignmentToClient}
            assignmentId={assignmentId}
          />
        </div>
      }

    </Fragment>
  )
}
